import { Link } from "react-router-dom";
import type { BlogPostView } from "../../hooks/use-blog-posts";

// RelatedPosts
// Renders up to 2 sibling blog cards beneath a blog post body. Ported from
// the inline `RelatedPosts` component in dane's blog/post/page.tsx. Now driven
// by the live API payload (`BlogPostView[]`) passed in by the page — no static
// data. Renders nothing when there are no other posts to show.

interface RelatedPostsProps {
  currentSlug: string;
  /** Pool of posts to pick "more like this" from. */
  pool: BlogPostView[];
  /** Max cards to render (default 2). */
  limit?: number;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function readTimeLabel(minutes: number): string {
  return `${minutes} min read`;
}

export function RelatedPosts({ currentSlug, pool, limit = 2 }: RelatedPostsProps) {
  const others = pool.filter((p) => p.slug !== currentSlug).slice(0, limit);
  if (others.length === 0) return null;

  return (
    <div className="border-t border-[var(--etf-border)] py-12 px-10 max-md:py-9 max-md:px-5 bg-[var(--etf-black2)]">
      <div className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.16em] uppercase text-[var(--etf-white3)] mb-7 flex items-center gap-4 after:content-[''] after:flex-1 after:h-px after:bg-[var(--etf-border)]">
        More From Corgi Insights
      </div>
      <div className="grid grid-cols-2 max-md:grid-cols-1 gap-px bg-[var(--etf-border)] border border-[var(--etf-border)]">
        {others.map((post) => (
          <Link
            key={post.slug}
            to={`/blog/${post.slug}`}
            className="bg-[var(--etf-black2)] p-6 no-underline text-inherit transition-colors duration-150 flex flex-col gap-2.5 hover:bg-[var(--etf-black3)]"
          >
            <div className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.12em] uppercase text-[var(--etf-orange)]">
              {post.category} · {formatDate(post.publishedAt)}
            </div>
            <div className="font-[family-name:var(--font-f37bolton)] text-xl leading-none text-[var(--etf-white)] tracking-[-0.02em]">
              {post.title}
            </div>
            <div className="font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white3)] mt-auto">
              {post.author} · {readTimeLabel(post.readTimeMinutes)}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
